import { Card } from '@starter/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { api, unwrap } from '../lib/api.ts';

/**
 * `/deployment` — which commit and which environment this deployment is
 * serving, in a card a person can read.
 *
 * It was the home page until A3 gave that address to the product. Moving it
 * rather than deleting it is deliberate: **this card is the floor of the qa
 * stage**. `smoke.spec.ts` reads the commit off it to prove the stand under
 * test is the one CI just deployed, and `responsive.spec.ts` uses it as the
 * one screen guaranteed to exist at every width. Both would have gone quiet
 * rather than red if the card had simply disappeared under a new home page —
 * which is the failure that does not announce itself.
 *
 * Public, as it was: it says nothing an anonymous visitor may not know, and a
 * health page behind a sign-in is a health page you cannot check when sign-in
 * is what broke.
 */
function Deployment() {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => unwrap(api.api.health.$get()),
  });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-ink">Starter</h1>
        <p className="mt-2 max-w-prose text-ink-muted">
          Trunk-based template: every pull request gets a dev-preview stand with its own Neon branch
          and bootstrapped data.
        </p>
      </div>

      <Card title="Deployment" data-testid="deployment-card">
        {health.isPending ? (
          <p className="text-sm text-ink-muted">Checking the API…</p>
        ) : health.isError ? (
          <p className="text-sm text-danger-text" data-testid="health-error">
            API unreachable: {health.error.message}
          </p>
        ) : (
          /*
            `minmax(0, 1fr)` rather than `1fr`: a grid track's minimum is its
            content, so the plain version cannot be narrower than the longest
            value in it — and the longest value here is a forty-character commit
            SHA in a monospace font, which is wider than a phone. The track then
            pushes the whole card sideways. Paired with `break-all` on the SHA,
            because a hash has nowhere to wrap on its own.
          */
          <dl
            className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1 text-sm"
            data-testid="health"
          >
            <dt className="text-ink-muted">Environment</dt>
            <dd className="font-mono text-ink" data-testid="health-env">
              {health.data.appEnv}
            </dd>
            <dt className="text-ink-muted">Commit</dt>
            <dd className="break-all font-mono text-ink" data-testid="health-sha">
              {health.data.gitSha}
            </dd>
            <dt className="text-ink-muted">Uptime</dt>
            <dd className="font-mono text-ink">{health.data.uptimeSeconds}s</dd>
            <dt className="text-ink-muted">Database</dt>
            <dd className="font-mono text-ink" data-testid="health-db">
              {health.data.appEnv === 'preview' ? 'schema-only branch' : 'production branch'}
            </dd>
          </dl>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/deployment')({ component: Deployment });
